import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./App.css";

const DRUPAL_BASE = "https://musicosity11.audienceware.com";
const DRUPAL_API = `${DRUPAL_BASE}/jsonapi`;

function App() {
  const [events, setEvents] = useState([]);
  const [artists, setArtists] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const getDrupalImage = (node, included, type) => {
    const imageData = node.relationships?.field_image?.data;
    if (!imageData || (Array.isArray(imageData) && imageData.length === 0)) {
      const presets = { event: 'concert', artist: 'portrait', venue: 'architecture' };
      return `https://loremflickr.com/800/600/${presets[type] || 'music'}?lock=${node.attributes.drupal_internal__nid}`;
    }
    const imageId = Array.isArray(imageData) ? imageData[0].id : imageData.id;
    const file = included?.find(item => item.id === imageId);
    if (file && file.attributes?.uri?.url) {
      const path = file.attributes.uri.url;
      return path.startsWith('http') ? path : `${DRUPAL_BASE}${path}`;
    }
    return `https://via.placeholder.com/500x700?text=No+Image`;
  };

  const formatEventDate = (iso) => {
    if (!iso) return "TBA";
    const date = new Date(iso);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }).toUpperCase() + 
           " • " + 
           date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const artistURL = `${DRUPAL_API}/node/artist?include=field_image&filter[img][condition][path]=field_image&filter[img][condition][operator]=IS%20NOT%20NULL&page[limit]=12`;
        
        const [evRes, artRes, venRes] = await Promise.all([
          axios.get(`${DRUPAL_API}/node/event?sort=field_event_date.value&page[limit]=12`),
          axios.get(artistURL),
          axios.get(`${DRUPAL_API}/node/venue?sort=-created&page[limit]=50`)
        ]);

        setEvents(evRes.data.data.map(item => ({
          id: item.id,
          type: 'Event',
          title: item.attributes.title,
          date: formatEventDate(item.attributes.field_event_date?.value),
          desc: item.attributes.body?.processed || "No description available.",
          image: `https://loremflickr.com/800/500/concert,dark?lock=${item.attributes.drupal_internal__nid}`
        })));

        setArtists(artRes.data.data.map(item => ({
          id: item.id,
          type: 'Artist',
          title: item.attributes.title,
          desc: "Featured Professional Artist",
          image: getDrupalImage(item, artRes.data.included, 'artist')
        })));

        const allVenues = venRes.data.data.map(item => {
          const loc = item.attributes.field_location;
          let displayLocation = "Location details coming soon";

          if (loc && typeof loc === 'object') {
            const parts = [loc.address_line1, loc.locality, loc.administrative_area, loc.postal_code]
              .filter(part => part && part.trim() !== "");
            if (parts.length > 0) displayLocation = parts.join(", ");
          }

          return {
            id: item.id,
            type: 'Venue',
            title: item.attributes.title,
            location: displayLocation, 
            desc: item.attributes.body?.processed || "Join us at this premier venue.",
            image: `https://loremflickr.com/700/500/architecture,dark?lock=${item.attributes.drupal_internal__nid}`
          };
        });

        setVenues(allVenues);
        setLoading(false);
      } catch (err) { 
        console.error("Error fetching data:", err);
        setLoading(false); 
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (searchTerm && searchTerm.length > 0) {
      const resultsArea = document.getElementById("results-area");
      if (resultsArea) {
        setTimeout(() => {
          resultsArea.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [searchTerm]);

  const hasMatches = (items) => 
    items.some(i => i.title.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return <div className="loading-screen">MUSICOSITY...</div>;

  return (
    <div className="frame-app">
      <nav className="navbar">
        <div className="nav-logo" onClick={() => {setSearchTerm(""); window.scrollTo({top:0, behavior:'smooth'})}}>
        <img 
        src="https://www.musicosity.com.au/sites/default/files/Musicosity_35.png" 
        alt="Musicosity Logo" 
        className="logo-img" 
        />
          </div>
        <div className="nav-menu">
          <button onClick={() => {setSearchTerm(""); window.scrollTo({top:0, behavior:'smooth'})}}>HOME</button>
          <button onClick={() => document.getElementById('shows-sec').scrollIntoView({behavior: 'smooth'})}>EVENTS</button>
          <button onClick={() => document.getElementById('artists-sec').scrollIntoView({behavior: 'smooth'})}>ARTISTS</button>
          <button onClick={() => document.getElementById('venues-sec').scrollIntoView({behavior: 'smooth'})}>VENUES</button>
        </div>
        <div className="search-container">
          <input 
            type="text" 
            placeholder="Search events, artists..." 
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </nav>

   {/* MODAL */}
{selectedItem && (
  <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
    <div className="modal-content" onClick={e => e.stopPropagation()}>
      <button className="close-btn" onClick={() => setSelectedItem(null)}>×</button>
      <div className="modal-scroll-area">
        <img src={selectedItem.image} alt={selectedItem.title} className="modal-banner" />
        
        <div className="modal-text">
          <span className="mini-label">{selectedItem.type}</span>
          <h2 className="modal-display-title">{selectedItem.title}</h2>
          
          {/* Show Date for Events */}
          {selectedItem.date && <p className="modal-meta"><strong>WHEN:</strong> {selectedItem.date}</p>}
          
          {/* Show Location and Map Button for Venues */}
          {selectedItem.type === 'Venue' && selectedItem.location && (
            <div className="modal-venue-info">
              <p className="modal-meta"><strong>WHERE:</strong> {selectedItem.location}</p>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedItem.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="frame-cta map-btn"
                style={{ display: 'inline-block', marginTop: '10px', textDecoration: 'none' }}
              >
                VIEW ON GOOGLE MAPS
              </a>
            </div>
          )}

          <hr className="modal-divider" />
          
          {/* Description */}
          <div 
            className="modal-desc-full" 
            dangerouslySetInnerHTML={{ __html: selectedItem.desc }} 
          />
        </div>
      </div>
    </div>
  </div>
)}

      {/* HERO - RESTORED CODE */}
      {!searchTerm && events[0] && (
        <header className="hero-frame">
          <div className="hero-grid">
            <div className="hero-text-side">
              <span className="mini-label">FEATURED EVENT</span>
              <h1 className="hero-display-title">{events[0].title}</h1>
              <p className="hero-meta">{events[0].date}</p>
              <button className="frame-cta" onClick={() => setSelectedItem(events[0])}>VIEW DETAILS</button>
            </div>
            <div className="hero-image-side">
              <img src={events[0].image} alt="Featured" />
            </div>
          </div>
        </header>
      )}

      <main className="content-rows" id="results-area">
        {hasMatches(events) && (
          <div id="shows-sec">
            <AutoRow 
              title="Upcoming Shows" 
              items={events.filter(i => i.title.toLowerCase().includes(searchTerm.toLowerCase()))} 
              type="event" 
              onSelect={setSelectedItem} 
            />
          </div>
        )}

        {hasMatches(artists) && (
          <div id="artists-sec">
            <AutoRow 
              title="Artist Spotlights" 
              items={artists.filter(i => i.title.toLowerCase().includes(searchTerm.toLowerCase()))} 
              type="artist" 
              onSelect={setSelectedItem} 
            />
          </div>
        )}

        {hasMatches(venues) && (
          <div id="venues-sec">
            <AutoRow 
              title="Signature Venues" 
              items={venues.filter(i => i.title.toLowerCase().includes(searchTerm.toLowerCase()))} 
              type="venue" 
              onSelect={setSelectedItem} 
            />
          </div>
        )}

        {searchTerm && !hasMatches(events) && !hasMatches(artists) && !hasMatches(venues) && (
          <div className="no-results">
            <h2>No matches found for "{searchTerm}"</h2>
            <button onClick={() => setSearchTerm("")} className="frame-cta">CLEAR SEARCH</button>
          </div>
        )}
      </main>
    </div>
  );
}

function AutoRow({ title, items, type, onSelect }) {
  const rowRef = useRef(null);

  useEffect(() => {
    const scrollContainer = rowRef.current;
    if (!scrollContainer || items.length === 0) return;

    const interval = setInterval(() => {
      const scrollAmount = type === "artist" ? 380 : 480;
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;

      if (scrollContainer.scrollLeft >= maxScrollLeft - 10) {
        scrollContainer.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        scrollContainer.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [items, type]);

  const scrollRow = (dir) => {
    if (rowRef.current) {
      const scrollAmount = type === "artist" ? 380 : 480;
      rowRef.current.scrollBy({
        left: dir === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="frame-row">
      <h2 className="frame-row-title">{title}</h2>
      <div className="slider-wrapper">
        <button className="nav-arrow prev" onClick={() => scrollRow("left")}>❮</button>
        <div className="frame-shelf" ref={rowRef}>
          {items.map((item) => (
            <div key={item.id} className={`frame-item ${type}-box`} onClick={() => onSelect(item)}>
              <div className="img-wrapper">
                <img src={item.image} alt={item.title} />
                <div className="hover-overlay"></div>
              </div>
              <div className="frame-info-block">
                {item.date && <span className="frame-sub">{item.date}</span>}
                <h3 className="frame-main-name">{item.title}</h3>
                {item.location && <p className="venue-loc-preview">{item.location}</p>}
              </div>
            </div>
          ))}
        </div>
        <button className="nav-arrow next" onClick={() => scrollRow("right")}>❯</button>
      </div>
    </section>
  );
}

export default App;